<template>
  <div class="account-creation-container">
    <AppLogo />
    <PageHeader>Создать аккаунт</PageHeader>
    <form @submit.prevent="saveAccount" novalidate>
      <InputField type="text" v-model="firstName" placeholder="Имя" />
      <p v-if="errors.username" class="field-error">{{ errors.username[0] }}</p>

      <InputField type="text" v-model="lastName" placeholder="Фамилия" />
      <p v-if="errors.user_lastname" class="field-error">{{ errors.user_lastname[0] }}</p>

      <InputField type="password" v-model="password" placeholder="Введите пароль" />
      <p
          class="field-hint"
          :class="{ 'field-hint--error': passwordTouched && password.length > 0 && password.length < 8 }"
          v-if="!errors.password"
      >
        Пароль должен содержать не менее 8 символов
      </p>
      <p v-if="errors.password" class="field-error">{{ errors.password[0] }}</p>

      <div class="checkbox-container">
        <input type="checkbox" v-model="isPrivate" />
        <span class="checkbox-text">Частное лицо</span>
      </div>
      <div>
        <input
            type="text"
            v-model="company"
            placeholder="Компания"
            :disabled="isPrivate"
            :class="{ 'disabled-input': isPrivate }"
        />
      </div>
      <p v-if="errors.company" class="field-error">{{ errors.company[0] }}</p>
      <div>
        <select
            v-model="position"
            :disabled="isPrivate"
            :class="{ 'disabled-input': isPrivate }"
        >
          <option value="" disabled selected>Должность</option>
          <option value="manager">Менеджер</option>
          <option value="developer">Разработчик</option>
          <option value="designer">Дизайнер</option>
        </select>
      </div>
      <p v-if="errors.position" class="field-error">{{ errors.position[0] }}</p>

      <p v-if="errors.email" class="field-error">{{ errors.email[0] }}</p>
      <p v-if="generalError" class="field-error field-error--general">{{ generalError }}</p>

      <button type="submit" class="continue-button" :disabled="isSubmitting">
        {{ isSubmitting ? 'Сохранение...' : 'Сохранить' }}
      </button>

      <div class="checkbox-container">
        <input type="checkbox" v-model="subscribeToNews" />
        <span class="checkbox-text">Подписаться на новости рынка и обновления аналитических материалов</span>
      </div>

      <InstructionHelp>
        Нажимая «Сохранить», вы подтверждаете, что согласны на обработку персональных данных и ознакомились с
        <router-link to="/privacy-policy" class="privacy-policy-link">политикой конфиденциальности</router-link>
      </InstructionHelp>
    </form>
  </div>
</template>

<script>
import AppLogo from "@/components/auth/AppLogo.vue";
import PageHeader from "@/components/auth/PageHeader.vue";
import InputField from "@/components/auth/InputField.vue";
import InstructionHelp from "@/components/auth/InstructionHelp.vue";
import { reachGoal } from "@/utils/analytics";

// Переводы стандартных сообщений валидации Laravel в русские фразы.
// Ключ — подстрока из англ. сообщения, значение — русский текст.
const VALIDATION_MESSAGE_MAP = [
  { re: /must be at least (\d+) characters/i, ru: (m, field) => `${field} должен содержать не менее ${m[1]} символов` },
  { re: /may not be greater than (\d+) characters/i, ru: (m, field) => `${field} не должен превышать ${m[1]} символов` },
  { re: /field is required/i, ru: (_m, field) => `Поле «${field}» обязательно для заполнения` },
  { re: /must be a valid email/i, ru: () => `Введите корректный email` },
  { re: /has already been taken/i, ru: (_m, field) => `${field} уже используется` },
  { re: /confirmation does not match/i, ru: () => `Пароли не совпадают` },
];

const FIELD_LABELS = {
  password: 'Пароль',
  email: 'Email',
  username: 'Имя',
  user_lastname: 'Фамилия',
  company: 'Компания',
  position: 'Должность',
};

function translateValidationMessage(field, message) {
  const label = FIELD_LABELS[field] || field;
  for (const { re, ru } of VALIDATION_MESSAGE_MAP) {
    const m = message.match(re);
    if (m) return ru(m, label);
  }
  return message;
}

export default {
  components: {InstructionHelp, InputField, PageHeader, AppLogo},
  data() {
    return {
      firstName: '',
      lastName: '',
      password: '',
      isPrivate: false,
      company: '',
      position: '',
      subscribeToNews: false,
      errors: {},
      generalError: '',
      isSubmitting: false,
      passwordTouched: false,
    };
  },
  watch: {
    password(val) {
      if (val.length > 0) this.passwordTouched = true;
      // Сбрасываем серверную ошибку пароля при новом вводе
      if (this.errors.password) {
        const rest = { ...this.errors };
        delete rest.password;
        this.errors = rest;
      }
    },
  },
  methods: {
    validateClient() {
      const errors = {};
      if (!this.firstName.trim()) errors.username = ['Введите имя'];
      if (!this.lastName.trim()) errors.user_lastname = ['Введите фамилию'];
      if (!this.password || this.password.length < 8) {
        errors.password = ['Пароль должен содержать не менее 8 символов'];
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async saveAccount() {
      this.generalError = '';
      this.passwordTouched = true;
      if (!this.validateClient()) return;

      this.isSubmitting = true;
      try {
        const email = localStorage.getItem('email');
        const response = await fetch(`${process.env.VUE_APP_URL_DEV}/api/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: this.firstName,
            user_lastname: this.lastName,
            email: email,
            password: this.password,
            user_type: this.isPrivate ? 'individual' : 'legal_entity',
            company: this.company === '' ? null : this.company,
            position: this.position === '' ? null : this.position,
            is_subscribed: this.subscribeToNews
          })
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (response.status === 422 && data && data.errors) {
            // Переводим сообщения валидации Laravel
            const translated = {};
            for (const [field, messages] of Object.entries(data.errors)) {
              translated[field] = messages.map((m) => translateValidationMessage(field, m));
            }
            this.errors = translated;
          } else {
            this.generalError = (data && data.message) || 'Ошибка регистрации. Попробуйте ещё раз.';
          }
          return;
        }

        const accessToken = data['data']['access_token'];
        const userId = data['data']['user_id'];

        localStorage.setItem('token', accessToken);

        const userResponse = await fetch(`${process.env.VUE_APP_URL_DEV}/api/users/${userId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!userResponse.ok) {
          this.generalError = 'Ошибка получения данных пользователя';
          return;
        }

        const userInfo = await userResponse.json();

        this.$store.commit('main/setUserInfo', userInfo.data);

        // Цель «Создание аккаунта» — регистрация успешно завершена.
        reachGoal('account_create', {
          user_type: this.isPrivate ? 'individual' : 'legal_entity',
        });

        this.$router.push('/chat');

      } catch (error) {
        this.generalError = 'Не удалось выполнить запрос. Проверьте соединение и попробуйте снова.';
        console.log(error.message);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.account-creation-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100vh;
  max-width: 400px;
  margin: auto;
  text-align: center;
}

form {
  display: flex;
  flex-direction: column;
}

input[type="text"],
select {
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 6px;
}

.continue-button {
  background-color: #000;
  color: #fff;
  padding: 10px;
  font-size: 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 15px;
  font-weight: bold;
}

.continue-button:hover {
  background-color: #333;
}

.continue-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.checkbox-container {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  text-align: left;
}

.checkbox-container input {
  margin-right: 10px;
}

.privacy-policy-link {
  text-decoration: underline;
  color: #000;
  cursor: pointer;
}

.disabled-input {
  background-color: #f0f0f0;
  cursor: not-allowed;
}

.field-error {
  color: #d33;
  font-size: 12px;
  margin: -10px 0 10px;
  text-align: left;
}

.field-error--general {
  margin: 0 0 10px;
  text-align: center;
}

.field-hint {
  color: #888;
  font-size: 12px;
  margin: -10px 0 10px;
  text-align: left;
}

.field-hint--error {
  color: #d33;
}
</style>
